import {
  CloseButton as ChakraCloseButton,
  DrawerCloseButton as ChakraDrawerCloseButton,
  ModalCloseButton as ChakraModalCloseButton,
  PopoverCloseButton as ChakraPopoverCloseButton,
  TagCloseButton as ChakraTagCloseButton,
  type CloseButtonProps,
  type ModalCloseButtonProps,
  type PopoverCloseButtonProps,
  type TagCloseButtonProps,
} from '@chakra-ui/react';
import {TablerX} from '../../icons';

type CloseIconSize = number | string;

interface TablerCloseIconProps {
  size?: CloseIconSize;
}

interface TablerCloseButtonIconProps {
  iconSize?: CloseIconSize;
}

const HEADING_CLOSE_BUTTON_GEOMETRY = {
  sm: {
    boxSize: '1rem',
    iconSize: 14,
    top: '1.25rem',
    xAlignOffset: '0.0625rem',
  },
  md: {
    boxSize: '1.25rem',
    iconSize: 16,
    top: '1.125rem',
    xAlignOffset: '0.125rem',
  },
  lg: {
    boxSize: '1.5rem',
    iconSize: 16,
    top: '1rem',
    xAlignOffset: '0.25rem',
  },
};

type HeadingCloseButtonGeometrySize =
  keyof typeof HEADING_CLOSE_BUTTON_GEOMETRY;

const isHeadingCloseButtonGeometrySize = (
  size: unknown,
): size is HeadingCloseButtonGeometrySize =>
  size === 'sm' || size === 'md' || size === 'lg';

const headingCloseButtonGeometry = (size: unknown) => {
  if (isHeadingCloseButtonGeometrySize(size))
    return HEADING_CLOSE_BUTTON_GEOMETRY[size];
  return HEADING_CLOSE_BUTTON_GEOMETRY.md;
};

const TablerCloseIcon = ({size = 16}: TablerCloseIconProps) => (
  <TablerX aria-hidden="true" focusable="false" size={size} />
);

export const TablerCloseButton = ({
  children,
  iconSize = 16,
  ...props
}: CloseButtonProps & TablerCloseButtonIconProps) => (
  <ChakraCloseButton {...props}>
    {children ?? <TablerCloseIcon size={iconSize} />}
  </ChakraCloseButton>
);

export const TablerDrawerCloseButton = ({
  boxSize,
  children,
  h,
  iconSize,
  insetEnd,
  minW,
  p,
  right,
  size = 'md',
  top,
  transform,
  ...props
}: ModalCloseButtonProps & TablerCloseButtonIconProps) => (
  <ChakraDrawerCloseButton
    {...props}
    boxSize={boxSize ?? headingCloseButtonGeometry(size).boxSize}
    h={h ?? boxSize ?? headingCloseButtonGeometry(size).boxSize}
    minW={minW ?? boxSize ?? headingCloseButtonGeometry(size).boxSize}
    p={p ?? 0}
    right={right ?? insetEnd ?? 6}
    size={size}
    top={top ?? headingCloseButtonGeometry(size).top}
    transform={
      transform ??
      `translateX(${headingCloseButtonGeometry(size).xAlignOffset})`
    }
  >
    {children ?? (
      <TablerCloseIcon
        size={iconSize ?? headingCloseButtonGeometry(size).iconSize}
      />
    )}
  </ChakraDrawerCloseButton>
);

export const TablerModalCloseButton = ({
  boxSize,
  children,
  h,
  iconSize,
  insetEnd,
  minW,
  p,
  right,
  size = 'md',
  top,
  transform,
  ...props
}: ModalCloseButtonProps & TablerCloseButtonIconProps) => (
  <ChakraModalCloseButton
    {...props}
    boxSize={boxSize ?? headingCloseButtonGeometry(size).boxSize}
    h={h ?? boxSize ?? headingCloseButtonGeometry(size).boxSize}
    minW={minW ?? boxSize ?? headingCloseButtonGeometry(size).boxSize}
    p={p ?? 0}
    right={right ?? insetEnd ?? 6}
    size={size}
    top={top ?? headingCloseButtonGeometry(size).top}
    transform={
      transform ??
      `translateX(${headingCloseButtonGeometry(size).xAlignOffset})`
    }
  >
    {children ?? (
      <TablerCloseIcon
        size={iconSize ?? headingCloseButtonGeometry(size).iconSize}
      />
    )}
  </ChakraModalCloseButton>
);

export const TablerPopoverCloseButton = ({
  boxSize,
  children,
  h,
  iconSize,
  minW,
  p,
  size = 'md',
  ...props
}: PopoverCloseButtonProps & TablerCloseButtonIconProps) => (
  <ChakraPopoverCloseButton
    {...props}
    boxSize={boxSize ?? headingCloseButtonGeometry(size).boxSize}
    h={h ?? boxSize ?? headingCloseButtonGeometry(size).boxSize}
    minW={minW ?? boxSize ?? headingCloseButtonGeometry(size).boxSize}
    p={p ?? 0}
    size={size}
  >
    {children ?? (
      <TablerCloseIcon
        size={iconSize ?? headingCloseButtonGeometry(size).iconSize}
      />
    )}
  </ChakraPopoverCloseButton>
);

export const TablerTagCloseButton = ({
  children,
  iconSize = 13,
  ...props
}: TagCloseButtonProps & TablerCloseButtonIconProps) => (
  <ChakraTagCloseButton {...props}>
    {children ?? <TablerCloseIcon size={iconSize} />}
  </ChakraTagCloseButton>
);
